import { motion, AnimatePresence } from 'motion/react';
import brandwImage from '@/assets/xReplAI-brandw.png';
import { ArrowRight, Chrome } from 'lucide-react';
import { useState, useEffect } from 'react';
import { WaitlistModal } from './WaitlistModal';
import { useMaintenanceCTA } from '../hooks/useMaintenanceCTA';

export function Hero() {
  const [currentWordIndex, setCurrentWordIndex] = useState(0);
  const words = ['Replies', 'Drafts', 'Forwards'];
  const { isWaitlistOpen, setIsWaitlistOpen, handleCTA } = useMaintenanceCTA();

  // Professional gradient background using inline styles (no external image)
  const heroBkg = `data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 1920 1080'%3E%3Cdefs%3E%3ClinearGradient id='grad' x1='0%25' y1='0%25' x2='100%25' y2='100%25'%3E%3Cstop offset='0%25' style='stop-color:%230A1628;stop-opacity:1' /%3E%3Cstop offset='50%25' style='stop-color:%231E3A8A;stop-opacity:0.8' /%3E%3Cstop offset='100%25' style='stop-color:%230A1628;stop-opacity:1' /%3E%3C/linearGradient%3E%3C/defs%3E%3Crect width='1920' height='1080' fill='url(%23grad)'/%3E%3Ccircle cx='400' cy='200' r='300' fill='%2360A5FA' opacity='0.05'/%3E%3Ccircle cx='1520' cy='880' r='400' fill='%2360A5FA' opacity='0.05'/%3E%3C/svg%3E`;

  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentWordIndex((prev) => (prev + 1) % words.length);
    }, 2500);
    return () => clearInterval(interval);
  }, []);

  return (
    <section
      id="home"
      className="min-h-screen flex items-center justify-center relative overflow-hidden"
    >
      {/* Removed background image and backgroundColor - using parent LandingPage background */}
      
      {/* Dark overlay for better text readability */}
      <div className="absolute inset-0 bg-[#0A1628]/20" />

      {/* Animated background elements */}
      <div className="absolute inset-0 overflow-hidden">
        <motion.div
          className="absolute top-1/4 left-1/4 w-96 h-96 bg-[#60A5FA]/10 rounded-full blur-3xl"
          animate={{
            scale: [1, 1.2, 1],
            opacity: [0.3, 0.5, 0.3]
          }}
          transition={{
            duration: 8,
            repeat: Infinity,
            ease: 'easeInOut'
          }}
        />
        <motion.div
          className="absolute bottom-1/4 right-1/4 w-96 h-96 bg-[#1E3A8A]/10 rounded-full blur-3xl"
          animate={{
            scale: [1.2, 1, 1.2],
            opacity: [0.5, 0.3, 0.5]
          }}
          transition={{
            duration: 8,
            repeat: Infinity,
            ease: 'easeInOut'
          }}
        />
      </div>

      <div className="relative z-10 max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-20 text-center">
        <motion.div
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.5 }}
          className="flex items-center justify-center mb-8"
        >
          <img
            src={brandwImage}
            alt="xReplAI"
            className="h-16 sm:h-20 lg:h-24 w-auto object-contain"
          />
        </motion.div>

        <motion.h1
          className="text-5xl sm:text-6xl lg:text-7xl mb-6 flex flex-wrap items-center justify-center gap-3"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.2 }}
        >
          <span className="text-white font-light">Smart</span>
          <span className="relative inline-block min-w-[280px] sm:min-w-[320px] lg:min-w-[380px] text-center">
            <AnimatePresence mode="wait">
              <motion.span
                key={words[currentWordIndex]}
                initial={{ x: 50, opacity: 0 }}
                animate={{ x: 0, opacity: 1 }}
                exit={{ x: -50, opacity: 0 }}
                transition={{ duration: 0.5 }}
                className="inline-block font-semibold text-[#60A5FA]"
              >
                {words[currentWordIndex]}
              </motion.span>
            </AnimatePresence>
          </span>
          <span className="text-gray-100 font-light">Instantly</span>
        </motion.h1>

        <motion.p
          className="text-xl sm:text-2xl text-gray-200 mb-12 max-w-3xl mx-auto leading-relaxed"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.4 }}
        >
          AI that drafts professional, high-converting email replies in your voice — right in Gmail & LinkedIn.
        </motion.p>

        <motion.div
          className="flex flex-col sm:flex-row gap-4 justify-center items-center"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.6 }}
        >
          <button 
            onClick={() => handleCTA(() => {
              // Open the xReplAI Chrome Web Store listing
              window.open('https://chromewebstore.google.com/detail/aeemmefmpkgmokecemoedojagcecabmj', '_blank');
            })}
            className="group px-8 py-4 bg-[#60A5FA] text-white rounded-lg hover:bg-[#60A5FA]/90 transition-all hover:shadow-2xl hover:shadow-[#60A5FA]/30 flex items-center gap-2"
          >
            <Chrome size={20} />
            Install Free Extension
            <ArrowRight
              size={20}
              className="group-hover:translate-x-1 transition-transform"
            />
          </button>
          <button 
            onClick={() => handleCTA(() => {
              // Normal action: scroll to pricing
              document.getElementById('pricing')?.scrollIntoView({ behavior: 'smooth' });
            })}
            className="px-8 py-4 bg-transparent text-white border-2 border-white/30 rounded-lg hover:bg-white/10 transition-all backdrop-blur-sm"
          >
            View Pricing
          </button>
        </motion.div>

        <WaitlistModal isOpen={isWaitlistOpen} onClose={() => setIsWaitlistOpen(false)} />

        {/* Scroll indicator */}
        <motion.div
          className="absolute bottom-8 left-1/2 -translate-x-1/2"
          animate={{ y: [0, 10, 0] }}
          transition={{ duration: 2, repeat: Infinity }}
        >
          <div className="w-6 h-10 border-2 border-white/30 rounded-full flex justify-center pt-2">
            <div className="w-1.5 h-3 bg-white/50 rounded-full" />
          </div>
        </motion.div>
      </div>
    </section>
  );
}